import { useGetQueuesByAccountIdQuery } from '@/services/queues';
import { useMemo } from 'react';

import { QueueDocument, QueueDocumentResponse, QueueHistoryAction, QueueStatus, QueueType } from 'types';

/** This ITC specific hook checks if a project was submitted for install before 2026 (Midnight PST / 8 AM UTC). */
export const useProjectSubmittedBefore2026 = (accountId: string) => {
  const { data: queuesData } = useGetQueuesByAccountIdQuery({
    accountId,
    types: [QueueType.installPackage],
  });
  const installQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.installPackage);

  const projectSubmittedBefore2026 = useMemo(() => {
    if (!installQueue) return false;

    const firstSubmittedDate = getFirstStatusDate(installQueue, QueueStatus.submitted);
    if (!firstSubmittedDate) return false;

    // 8 AM UTC - 12 PM PST
    return firstSubmittedDate < new Date('2026-01-01T08:00:00Z');
  }, [installQueue]);

  return projectSubmittedBefore2026;
};

export const getFirstStatusDate = (queue: QueueDocument, status: QueueStatus) => {
  const sortedHistory =
    queue.history
      ?.filter((h) => h.action === QueueHistoryAction.status && h.date)
      .toSorted((a, b) => {
        const dateA = typeof a.date === 'string' ? new Date(a.date) : a.date;
        const dateB = typeof b.date === 'string' ? new Date(b.date) : b.date;
        return dateA.getTime() - dateB.getTime();
      }) || [];

  const firstStatus = sortedHistory.find((h) => h.changedTo === status);
  if (!firstStatus?.date) return undefined;

  return typeof firstStatus.date === 'string' ? new Date(firstStatus.date) : firstStatus.date;
};
